import React, { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, ArrowLeft, ShoppingBag, DollarSign, Package, Building2, FileText, Upload, CheckCircle, XCircle } from "lucide-react";
import { purchasesApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { uploadFileToS3, generatePurchaseInvoiceKey, getS3BucketName } from "@/lib/s3";

export default function AdminPurchaseNew() {
  const navigate = useNavigate();
  const params = useParams();
  const { token } = useAuthStore();
  const purchaseId = params.id ? parseInt(params.id) : null;
  const isEdit = purchaseId !== null;

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [loading, setLoading] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [pendingInvoiceFile, setPendingInvoiceFile] = useState<File | null>(null);
  const [status, setStatus] = useState<"used" | "unused">("unused");
  const [formData, setFormData] = useState({
    item_name: "",
    quantity: "",
    price: "",
    vendor_name: "",
    reorder_id: "",
    description: "",
    purchase_date: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
  });

  React.useEffect(() => {
    if (isEdit && token) {
      fetchPurchase();
    }
  }, [isEdit, purchaseId, token]);

  const fetchPurchase = async () => {
    if (!purchaseId || !token) return;
    try {
      setLoading(true);
      const data = await purchasesApi.getPurchase(purchaseId, token);
      setFormData({
        item_name: data.item_name || "",
        quantity: String(data.quantity || ""),
        price: String(data.price || ""),
        vendor_name: data.vendor_name || "",
        reorder_id: data.reorder_id || "",
        description: data.description || "",
        purchase_date: data.purchase_date
          ? new Date(data.purchase_date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
      setInvoiceUrl(data.invoice_url || null);
      setStatus(data.status || "unused");
    } catch (error: any) {
      console.error("Error fetching purchase:", error);
      toast.error(error.response?.data?.detail || "Failed to load purchase");
      navigate("/admin/purchases");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleInvoiceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF, images, etc.)
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or image file");
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB limit");
      return;
    }

    setUploadingInvoice(true);

    try {
      const bucketName = getS3BucketName();
      
      // If editing, use existing purchaseId, otherwise store file temporarily
      if (isEdit && purchaseId) {
        // Generate S3 key for invoice
        const s3Key = generatePurchaseInvoiceKey(purchaseId, file.name);

        // Upload file directly to S3
        const uploadedUrl = await uploadFileToS3(file, s3Key, bucketName);

        // Update purchase with invoice URL
        await purchasesApi.updateInvoice(purchaseId, uploadedUrl, token!);
        
        setInvoiceUrl(uploadedUrl);
        toast.success("Invoice uploaded successfully!");
      } else {
        // For new purchases, store file to upload after creation
        setPendingInvoiceFile(file);
        toast("Invoice will be uploaded after purchase is created", { icon: "ℹ️" });
      }
    } catch (error: any) {
      console.error("Error uploading invoice:", error);
      toast.error(error.message || "Failed to upload invoice");
    } finally {
      setUploadingInvoice(false);
      // Reset file input
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleToggleStatus = async () => {
    if (!isEdit || !purchaseId || !token) {
      toast.error("Please save the purchase first");
      return;
    }

    const newStatus = status === "used" ? "unused" : "used";
    
    try {
      await purchasesApi.toggleStatus(purchaseId, newStatus, token);
      setStatus(newStatus);
      toast.success(`Purchase marked as ${newStatus}`);
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setLoading(true);

    try {
      const purchaseData = {
        item_name: formData.item_name,
        quantity: parseInt(formData.quantity) || 0,
        price: parseFloat(formData.price) || 0,
        vendor_name: formData.vendor_name,
        reorder_id: formData.reorder_id || null,
        description: formData.description || null,
        purchase_date: formData.purchase_date ? new Date(formData.purchase_date).toISOString() : null,
        invoice_url: invoiceUrl,
        status: status,
      };

      if (isEdit && purchaseId) {
        await purchasesApi.updatePurchase(purchaseId, purchaseData, token);
        toast.success("Purchase updated successfully!");
        navigate("/admin/purchases");
      } else {
        const created = await purchasesApi.createPurchase(purchaseData, token);
        
        // Upload invoice if there's a pending file
        if (pendingInvoiceFile && created.id) {
          try {
            const bucketName = getS3BucketName();
            const s3Key = generatePurchaseInvoiceKey(created.id, pendingInvoiceFile.name);
            const uploadedUrl = await uploadFileToS3(pendingInvoiceFile, s3Key, bucketName);
            await purchasesApi.updateInvoice(created.id, uploadedUrl, token);
            toast.success("Purchase created and invoice uploaded successfully!");
          } catch (error: any) {
            console.error("Error uploading invoice:", error);
            toast.error("Purchase created but invoice upload failed. You can upload it later.");
          }
        } else {
          toast.success("Purchase created successfully!");
        }
        navigate("/admin/purchases");
      }
    } catch (error: any) {
      console.error("Error saving purchase:", error);
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} purchase`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/purchases"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aarsa-sage-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Purchases
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">{isEdit ? "Edit Purchase" : "Add New Purchase"}</h1>
        <p className="text-xs text-gray-500">
          {isEdit ? "Update purchase information" : "Record a new inventory purchase"}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <ShoppingBag className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Purchase Information</h2>
              </div>
            </div>
            <div className="p-4 space-y-2.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Item Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="item_name"
                  value={formData.item_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="e.g., Boxes, Shredded Paper, Stickers"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    required
                    min="0"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Vendor & Reference */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Vendor & Reference</h2>
              </div>
            </div>
            <div className="p-4 space-y-2.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="vendor_name"
                  value={formData.vendor_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="Enter vendor/supplier name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Reorder ID</label>
                <input
                  type="text"
                  name="reorder_id"
                  value={formData.reorder_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  placeholder="Enter reorder ID or reference number"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Purchase Date</label>
                <input
                  type="date"
                  name="purchase_date"
                  value={formData.purchase_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                />
              </div>
            </div>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Additional Notes</h2>
              </div>
            </div>
            <div className="p-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                  placeholder="Enter any additional notes or description about this purchase"
                />
              </div>
            </div>
          </motion.div>

          {/* Invoice & Status */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Invoice & Status</h2>
              </div>
            </div>
            <div className="p-4 space-y-4">
                {/* Invoice Upload */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Invoice</label>
                  {invoiceUrl ? (
                    <div className="flex items-center gap-3">
                      <a
                        href={invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        View Invoice
                      </a>
                      {isEdit && purchaseId && (
                        <label className="flex items-center gap-2 px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer text-sm">
                          <Upload className="w-4 h-4" />
                          {uploadingInvoice ? "Uploading..." : "Replace"}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleInvoiceUpload}
                            disabled={uploadingInvoice}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  ) : pendingInvoiceFile ? (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">
                        <FileText className="w-4 h-4" />
                        {pendingInvoiceFile.name}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingInvoiceFile(null)}
                        className="px-3 py-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-aarsa-sage-500 transition-colors cursor-pointer">
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {uploadingInvoice ? "Uploading invoice..." : "Upload Invoice (PDF or Image)"}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleInvoiceUpload}
                        disabled={uploadingInvoice}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Status Toggle */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Status</label>
                  {isEdit && purchaseId ? (
                    <>
                      <button
                        type="button"
                        onClick={handleToggleStatus}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          status === "used"
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {status === "used" ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">
                          {status === "used" ? "Mark as Unused" : "Mark as Used"}
                        </span>
                      </button>
                      <p className="text-xs text-gray-500 mt-1.5">
                        Current status: <span className="font-medium">{status === "used" ? "Used" : "Unused"}</span>
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setStatus(status === "used" ? "unused" : "used")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          status === "used"
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {status === "used" ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">
                          {status === "used" ? "Used" : "Unused"}
                        </span>
                      </button>
                      <p className="text-xs text-gray-500">
                        Status will be set when purchase is created
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-2 pb-2">
            <Link
              to="/admin/purchases"
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEdit ? "Update Purchase" : "Create Purchase"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

